import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { FramedArtwork } from "../components/FramedArtwork";
import { useI18n } from "../contexts/I18nContext";
import api, { fileUrl } from "../lib/api";
import { ArrowRight } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1664711942326-2c3351e215e6?w=900&q=70&fm=jpg&fit=crop&auto=format";
const INTERIOR_1 = "https://images.unsplash.com/photo-1664711942326-2c3351e215e6?w=900&q=62&fm=webp&fit=crop&auto=format";
const INITIAL_GALLERY_ITEMS = 12;

const hasStandardFraming = (artwork) => (artwork.standard_framing || []).some((o) => o.enabled);

const FramingBadge = ({ artwork, compact = false }) => {
  if (!hasStandardFraming(artwork)) return null;
  return (
    <div
      className={`inline-flex items-center border border-neutral-900 bg-neutral-950 text-white overline ${compact ? "text-[9px] px-2 py-1" : "text-[10px] px-2.5 py-1"}`}
      data-testid={`framing-badge-${artwork.id}`}
    >
      Disponible enmarcado
    </div>
  );
};

export default function HomePage() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [search, setSearch] = useState("");
  const [all, setAll] = useState([]);
  const [orientation, setOrientation] = useState("all"); // all | portrait | landscape | square
  const [visibleCount, setVisibleCount] = useState(INITIAL_GALLERY_ITEMS);

  const orientationOf = (a) => {
    if (!a.image_width || !a.image_height) return "unknown";
    const r = a.image_width / a.image_height;
    if (r < 0.92) return "portrait";
    if (r > 1.08) return "landscape";
    return "square";
  };
  const filteredAll = orientation === "all" ? all : all.filter((a) => orientationOf(a) === orientation);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (selectedCat !== "all") params.category = selectedCat;
    if (search) params.q = search;
    api.get("/artworks", { params }).then((r) => setAll(r.data)).catch(() => setAll([]));
  }, [selectedCat, search]);

  useEffect(() => {
    api.get("/artworks/featured", { params: { limit: 5 } }).then((r) => setFeatured(r.data)).catch(() => setFeatured([]));
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_GALLERY_ITEMS);
  }, [selectedCat, search, orientation]);

  return (
    <MarketplaceLayout>
      <div data-testid="home-page" className="sr-only" aria-hidden="true" />
      {/* HERO */}
      <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <div className="overline text-neutral-500 mb-8">{t.hero.overline}</div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-[8rem] leading-[0.95] tracking-tighter font-light text-neutral-950">
              {t.hero.title.split("\n").map((line, i) => (
                <span key={i} className="block">{line}</span>
              ))}
            </h1>
            <p className="mt-10 text-base sm:text-lg text-neutral-600 max-w-md leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#gallery" className="bg-black text-white px-8 py-4 overline hover:bg-neutral-800 transition-colors flex items-center gap-3 group" data-testid="hero-cta-explore">
                {t.hero.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/sell" className="border border-black text-black px-8 py-4 overline hover:bg-neutral-100 transition-colors" data-testid="hero-cta-sell">
                {t.hero.cta2}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={HERO_BG} alt="Gallery" loading="eager" decoding="async" fetchPriority="high" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO — d-Pod · The Pod · La cápsula */}
      <section className="border-t border-black/5 py-20 lg:py-28 bg-neutral-50/60" data-testid="manifesto">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="overline text-neutral-500 mb-6">Manifiesto</div>
              <div className="font-serif leading-none">
                <div className="text-5xl lg:text-6xl tracking-tight">
                  d-<span className="italic">Pod</span>
                </div>
                <div className="text-2xl lg:text-3xl text-neutral-500 mt-4 italic">The Pod</div>
                <div className="text-2xl lg:text-3xl text-neutral-500 mt-2 italic">La cápsula</div>
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <p className="text-lg lg:text-xl leading-relaxed text-neutral-700 font-serif">
                Cada obra vive dentro de su cápsula: se imprime <em>Print on Demand</em>, en
                edición limitada, sin stocks ni excedentes.
              </p>
              <p className="text-base text-neutral-600 mt-6 leading-relaxed max-w-2xl">
                Detrás de cada cápsula está{" "}
                <a href="https://123imprime.com" target="_blank" rel="noreferrer" className="text-neutral-900 underline underline-offset-4 hover:text-black">
                  d-Impresión
                </a>{" "}
                — nuestro taller de impresión fine art donde los píxeles se convierten en obras
                tangibles: papel de algodón etching, lienzo 390 gr, foto rag museo, tintas
                pigmentadas archivables. Veinte años imprimiendo para artistas y galerías, ahora
                al alcance directo del comprador.
              </p>
              <div className="mt-8 flex flex-wrap gap-6 overline text-xs">
                <Link to="/sell" className="inline-flex items-center gap-2 border-b border-black pb-1 hover:opacity-70 transition-opacity" data-testid="manifesto-cta-sell">
                  Publica tu obra <ArrowRight size={14} />
                </Link>
                <Link to="/talleres" className="inline-flex items-center gap-2 border-b border-neutral-300 pb-1 text-neutral-600 hover:text-black hover:border-black transition-colors" data-testid="manifesto-cta-workshops">
                  Talleres de enmarcación <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EDITORIAL */}
      {featured.length > 0 && (
        <section className="border-t border-black/5 py-24 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex items-baseline justify-between mb-16">
              <div>
                <div className="overline text-neutral-500 mb-4">N° 01 · Curated this week</div>
                <h2 className="font-serif text-4xl lg:text-6xl font-light tracking-tight">Editorial selection</h2>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-6 lg:gap-10">
              {featured[0] && (
                <Link to={`/artwork/${featured[0].id}`} className="col-span-12 lg:col-span-8 artwork-card group block" data-testid={`featured-artwork-${featured[0].id}`}>
                  <FramedArtwork
                    src={fileUrl(featured[0].image_path, "md")}
                    alt={featured[0].title}
                    ratio={featured[0].image_width && featured[0].image_height ? featured[0].image_width / featured[0].image_height : undefined}
                    aspectClass="aspect-[4/5]"
                  />
                  <div className="mt-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="overline text-neutral-500">{featured[0].category}</div>
                      <FramingBadge artwork={featured[0]} />
                    </div>
                    <h3 className="font-serif text-2xl mt-2">{featured[0].title}</h3>
                    <div className="text-sm text-neutral-500 mt-1">
                      {featured[0].artist_name} · {featured[0].sizes?.length > 1 ? "Desde " : ""}€{(featured[0].sizes?.length ? Math.min(...featured[0].sizes.map(s=>s.price)) : featured[0].price).toFixed(2)}
                    </div>
                  </div>
                </Link>
              )}
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-8">
                {featured.slice(1, 3).map((a) => (
                  <Link key={a.id} to={`/artwork/${a.id}`} className="artwork-card group block" data-testid={`featured-artwork-${a.id}`}>
                    <FramedArtwork
                      src={fileUrl(a.image_path, "sm")}
                      alt={a.title}
                      ratio={a.image_width && a.image_height ? a.image_width / a.image_height : undefined}
                      aspectClass="aspect-[4/5]"
                    />
                    <div className="mt-4">
                      <FramingBadge artwork={a} compact />
                      <h3 className="font-serif text-xl">{a.title}</h3>
                      <div className="text-sm text-neutral-500 mt-1">
                        {a.artist_name} · {a.sizes?.length > 1 ? "Desde " : ""}€{(a.sizes?.length ? Math.min(...a.sizes.map(s=>s.price)) : a.price).toFixed(2)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* INTERIOR CONTEXT */}
      <section className="bg-neutral-50 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline text-neutral-500 mb-6">N° 02 · The Process</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light leading-tight">
              Fine art print.<br />Calidad museo.
            </h2>
            <p className="mt-8 text-neutral-600 leading-relaxed max-w-md">
              Cada obra se imprime bajo demanda sobre <span className="text-neutral-900">papel etching 310gr</span> o <span className="text-neutral-900">lienzo canvas 100% algodón 390gr</span>, con tintas pigmentadas archivables. Cada pieza pasa por revisión de calidad antes del envío.
            </p>
            <p className="mt-4 text-neutral-600 leading-relaxed max-w-md">
              Cada obra se entrega con su <span className="text-neutral-900">certificado de autenticidad firmado por el artista</span>, y un servicio opcional de enmarcación profesional en talleres asociados.
            </p>
          </div>
          <div className="lg:col-span-7">
            <img src={INTERIOR_1} alt="Framed art on wall" loading="lazy" className="w-full aspect-[4/3] object-cover" />
          </div>
        </div>
      </section>

      {/* LOCAL & SUSTAINABLE */}
      <section className="bg-neutral-950 text-neutral-100 py-20 lg:py-28" data-testid="local-band">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline text-neutral-400 mb-6">N° 02b · Kilómetro cero</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Artistas locales.<br />
              <span className="italic text-neutral-300">Impresión sostenible.</span>
            </h2>
            <p className="mt-8 text-neutral-400 leading-relaxed max-w-md">
              Cada obra se imprime <strong className="text-white">bajo demanda en Alicante</strong>, sin stock previo ni transporte intercontinental. Apoyamos artistas locales que marcan su propio precio, y talleres de marco asociados de la zona.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-neutral-800">
            {[
              { label: "Print-on-demand", val: "Cero residuo, cero stock" },
              { label: "100% Alicante", val: "Taller propio, sin terceros" },
              { label: "Artistas locales", val: "Ellos marcan su precio" },
              { label: "Cultura de barrio", val: "Talleres de marco asociados" },
            ].map((b, i) => (
              <div key={i} className="bg-neutral-950 p-8">
                <div className="overline text-[10px] text-neutral-500">{b.label}</div>
                <div className="font-serif text-xl lg:text-2xl mt-2 text-white">{b.val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="py-24 lg:py-32 border-t border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
            <div>
              <div className="overline text-neutral-500 mb-4">N° 03 · Full archive</div>
              <h2 className="font-serif text-4xl lg:text-6xl font-light tracking-tight">{t.catalog.title}</h2>
            </div>
            <input
              type="text"
              placeholder={t.catalog.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-b border-neutral-300 focus:border-black bg-transparent py-2 outline-none w-full md:w-80 text-sm"
              data-testid="search-input"
            />
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 overline">
            <button
              onClick={() => setSelectedCat("all")}
              className={`pb-1 border-b ${selectedCat === "all" ? "border-black text-black" : "border-transparent text-neutral-400 hover:text-black"}`}
              data-testid="filter-all"
            >
              {t.catalog.all}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCat(c)}
                className={`pb-1 border-b ${selectedCat === c ? "border-black text-black" : "border-transparent text-neutral-400 hover:text-black"}`}
                data-testid={`filter-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Orientation filter */}
          <div className="flex flex-wrap items-center gap-x-1 gap-y-2 mb-12 overline text-[11px]">
            <span className="text-neutral-400 mr-3">Orientación</span>
            {[
              { id: "all", label: "Todas", iconBox: "w-4 h-3" },
              { id: "portrait", label: "Vertical", iconBox: "w-3 h-4" },
              { id: "landscape", label: "Horizontal", iconBox: "w-5 h-3" },
              { id: "square", label: "Cuadrada", iconBox: "w-3.5 h-3.5" },
            ].map((o) => {
              const active = orientation === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => setOrientation(o.id)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 border transition-colors ${
                    active
                      ? "bg-neutral-950 border-neutral-950 text-white"
                      : "border-neutral-200 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900"
                  }`}
                  data-testid={`orientation-${o.id}`}
                  aria-pressed={active}
                >
                  <span className={`inline-block ${o.iconBox} ${active ? "bg-white/90" : "bg-neutral-400 group-hover:bg-neutral-900"}`} />
                  {o.label}
                </button>
              );
            })}
          </div>

          {filteredAll.length === 0 ? (
            <div className="py-32 text-center text-neutral-400 overline" data-testid="empty-gallery">{t.catalog.empty}</div>
          ) : (
            <>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-20 items-start">
              {filteredAll.slice(0, visibleCount).map((a) => {
                const minPrice = a.sizes?.length ? Math.min(...a.sizes.map(s=>s.price)) : a.price;
                const ratio = a.image_width && a.image_height ? a.image_width / a.image_height : undefined;
                return (
                <Link key={a.id} to={`/artwork/${a.id}`} className="artwork-card group block" data-testid={`artwork-card-${a.id}`}>
                  <FramedArtwork src={fileUrl(a.image_path, "sm")} alt={a.title} ratio={ratio} aspectClass="aspect-[4/5]" imgTestId={`artwork-image-${a.id}`} />
                  <div className="mt-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="overline text-neutral-400">{a.category}</div>
                      <FramingBadge artwork={a} compact />
                    </div>
                    <h3 className="font-serif text-xl mt-1">{a.title}</h3>
                    <div className="text-sm text-neutral-500 mt-0.5">{a.artist_name}</div>
                    <div className="text-sm mt-1.5">{a.sizes?.length > 1 ? "Desde " : ""}€{minPrice.toFixed(2)}</div>
                  </div>
                </Link>
                );
              })}
            </div>
            {visibleCount < filteredAll.length && (
              <div className="mt-16 flex justify-center">
                <button
                  onClick={() => setVisibleCount((n) => n + INITIAL_GALLERY_ITEMS)}
                  className="border border-black px-8 py-4 overline hover:bg-neutral-950 hover:text-white transition-colors"
                  data-testid="load-more-artworks-button"
                >
                  Cargar más obras
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </section>
    </MarketplaceLayout>
  );
}
